import React, { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import { Button, Flex, Text, VStack } from '@chakra-ui/react'

import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { setDocumentDetailModal } from '@/stores/ui/uiSlice'

import useFile from '@/features/files/useFile'
import { allFileTypes } from '@/utils/constants'
import FieldWrapper from './FieldWrapper'

const VersionFileInput = ({
  name,
  fileType,
  label,
  canUpload,
  canDelete = canUpload,
  isMultiple = false,
  showVersion = true,
  showLatestFileName = true,
  allowedFileTypes = allFileTypes,
  DetailButtonText = 'Detail',
  onFilesSelected,
  ...rest
}) => {
  const [showDetailButton, setShowDetailButton] = useState(false)
  const [showUploadField, setShowUploadField] = useState(false)
  const [showNoUploadText, setShowNoUploadText] = useState(false)

  const [latestVersionFilesName, setLatestVersionFilesName] = useState('')
  const [tooltip, setTooltip] = useState('')

  const { control, getValues, setValue, register } = useFormContext()
  const { useUploadFile } = useFile()
  const dispatch = useDispatch()
  const uploadFile = useUploadFile()

  // Here Assume the documents object shd be populated from backend
  const documents = useWatch({ control, name }) ?? []

  const offerId = useWatch({ name: '_id' })

  useEffect(() => {
    register(name)
  }, [name, register])

  useEffect(() => {
    if (documents && documents.length > 0) {
      setShowDetailButton(true)
      setShowUploadField(false)
      setShowNoUploadText(false)

      const latestVersion = Math.max(...documents.map((doc) => doc.version))
      setLatestVersionFilesName(
        documents
          .filter((doc) => doc.version === latestVersion)
          .map((doc) => doc.originalFileName)
          .join(',')
      )
    } else {
      setShowDetailButton(false)
      setShowUploadField(canUpload)
      setShowNoUploadText(!canUpload)
    }
  }, [documents, canUpload])

  const fileChangedHandler = async (e) => {
    if (fileType && e.target.files.length > 0) {
      if (onFilesSelected) {
        onFilesSelected(e.target.files)
      }
      let docs = getValues(name)

      if (!docs) {
        // Handle Case for multiple input, EX: cost sheet / internal otheres
        docs = []
      }

      const filesArray = Array.from(e.target.files)

      dispatch(showLoadingOverlay())
      const res = await uploadFile.mutateAsync({ version: 0, type: fileType, files: filesArray }).then((res) => {
        const newVersionDocuments = [...docs, ...res.data]
        setValue(name, newVersionDocuments, { shouldDirty: false })
        dispatch(hideLoadingOverlay())
      })
    }
  }

  const viewFileDetail = async () => {
    dispatch(
      setDocumentDetailModal({
        show: true,
        title: label,
        path: name,
        fileType,
        isMultiple,
        canUpload,
        canDelete,
        offerId: offerId,
        showVersion: showVersion,
        allowedFileTypes: allowedFileTypes,
      })
    )
  }

  return (
    <FieldWrapper
      name={name}
      label={label}
      isRow
      {...rest}
      tooltip={
        <Flex gap="1" direction={'column'}>
          {documents.map((doc, index) => (
            <Text key={index}>{doc.originalFileName}</Text>
          ))}
        </Flex>
      }
    >
      {showDetailButton && (
        <Flex gap={1} alignItems={'center'} minW={0}>
          <Button colorScheme="blue" onClick={() => viewFileDetail()}>
            {DetailButtonText}
          </Button>
          {showLatestFileName && (
            <Text overflowX={'hidden'} textOverflow={'ellipsis'} whiteSpace={'nowrap'} maxW="250px">
              {latestVersionFilesName}
            </Text>
          )}
        </Flex>
      )}

      {showUploadField && (
        <input type="file" accept={allowedFileTypes} onChange={fileChangedHandler} multiple={isMultiple} />
      )}
      {showNoUploadText && <span>(No File Uploaded)</span>}
    </FieldWrapper>
  )
}

export default VersionFileInput
